/**
 * Badge shown next to email toggle headers in edit mode.
 * "✉ N email(s) sent" with a hover tooltip on the info icon showing recipient breakdown.
 */
import { Flex, Text } from '@chakra-ui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { Mail01, InfoCircle } from '@kula-ai/icons-core';
import { SCHEDULE_INTERVIEW_COPY } from '../config/copy';

export interface EmailRecipient {
  name: string;
  count: number;
}

export interface EmailSentBadgeProps {
  totalSent: number;
  recipients?: EmailRecipient[];
  showTooltip?: boolean;
}

export const EmailSentBadge = ({
  totalSent,
  recipients = [],
  showTooltip = true,
}: EmailSentBadgeProps) => {
  if (totalSent <= 0) return null;

  const label = SCHEDULE_INTERVIEW_COPY.communication.emailSentBadge(totalSent);
  const hasTooltip = showTooltip && recipients.length > 0;

  return (
    <Flex align="center" gap="1" color="secondary.500">
      <Mail01 size={16} />
      <Text fontSize="sm">{label}</Text>
      {hasTooltip && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Flex align="center" cursor="pointer" color="secondary.400">
                <InfoCircle size={16} />
              </Flex>
            </TooltipTrigger>
            <TooltipContent side="top">
              <Flex flexDir="column" gap="1">
                {recipients.map((r) => (
                  <Text key={r.name} fontSize="xs" color="white">
                    {r.count} email{r.count === 1 ? '' : 's'} to: {r.name}
                  </Text>
                ))}
              </Flex>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </Flex>
  );
};
